/**
 * Zamanlamalar sekmesi.
 *
 * Tablo:
 *   Ad | Tip | Cron | Subeler | Alicilar | Son Calisma | Sonraki | Durum | Islemler
 *
 * Islemler:
 *   - Test Mail Gonder (manuel tetikleme)
 *   - Duzenle
 *   - Sil
 *
 * Yeni zamanlama icin sag ust kose buton.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GroupIcon from '@mui/icons-material/Group';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  deleteSchedule,
  listSchedules,
  testSend,
} from '../services/mailReportsApi';
import ScheduleFormDialog from '../components/ScheduleFormDialog';
import type { ReportSchedule } from '../types';
import { humanizeCron } from '../utils/cronHumanizer';
import { formatLocalDateTime } from '../utils/dateFormat';
import { REPORT_KIND_OPTIONS } from '../types';

const SchedulesTab: React.FC = () => {
  const { branches, getBranchName } = useAuth();
  const [schedules, setSchedules] = React.useState<ReportSchedule[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [formOpen, setFormOpen] = React.useState(false);
  const [editingSchedule, setEditingSchedule] = React.useState<ReportSchedule | null>(null);

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    severity: 'success' | 'error' | 'info';
    message: string;
  }>({ open: false, severity: 'info', message: '' });

  const [testSendingId, setTestSendingId] = React.useState<number | null>(null);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<ReportSchedule | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSchedules();
      setSchedules(data);
    } catch (err: any) {
      setError(err?.response?.data?.Message || err?.message || 'Liste yuklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const handleNew = () => {
    setEditingSchedule(null);
    setFormOpen(true);
  };

  const handleEdit = (s: ReportSchedule) => {
    setEditingSchedule(s);
    setFormOpen(true);
  };

  const handleSaved = () => {
    setSnackbar({ open: true, severity: 'success', message: 'Kaydedildi.' });
    reload();
  };

  const requestDelete = (s: ReportSchedule) => {
    setDeleteTarget(s);
  };

  const cancelDelete = () => {
    if (deletingId !== null) return; // silme akisi devam ediyorsa kapatma
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const s = deleteTarget;
    setDeletingId(s.Id);
    try {
      await deleteSchedule(s.Id);
      setSnackbar({ open: true, severity: 'success', message: 'Zamanlama silindi' });
      setDeleteTarget(null);
      reload();
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.Message || err?.message || 'Silinemedi',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleTestSend = async (s: ReportSchedule) => {
    if (s.Recipients.length === 0) {
      setSnackbar({ open: true, severity: 'error', message: 'Bu zamanlamada alıcı yok.' });
      return;
    }
    setTestSendingId(s.Id);
    try {
      // Schedule cron'u her şube için AYRI mail gönderir; test-send'i de aynı davranışa uyduruyoruz:
      //  - BranchIds dolu -> N adet ayrı mail
      //  - BranchIds boş  -> 1 mail (Tüm Şubeler birleşik)
      const result = await testSend({
        To: s.Recipients,
        Cc: s.CcRecipients,
        BranchIds: s.BranchIds,
        ReportType: s.ReportType,
        CronExpression: s.CronExpression,
        TimeZoneId: s.TimeZoneId,
      });
      const detail =
        result.BranchCount > 1
          ? `${result.BranchCount} şube için ${result.BranchCount} ayrı mail`
          : `${result.ToCount} alıcı`;
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Test mail gönderildi (${detail}): ${result.Subject}`,
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.Message || err?.message || 'Test mail gönderilemedi',
      });
    } finally {
      setTestSendingId(null);
    }
  };

  // Schedule'in TimeZoneId'sine gore lokal saatte bicimle (Backend UTC gonderiyor).
  const fmtDateTime = (iso: string | null, tz?: string | null): string =>
    formatLocalDateTime(iso, tz);

  const reportTypeChip = (t: string) => {
    const label = t === 'weekly' ? 'Haftalık' : t === 'monthly' ? 'Aylık' : 'Günlük';
    const color: 'primary' | 'secondary' | 'info' = t === 'weekly' ? 'secondary' : t === 'monthly' ? 'info' : 'primary';
    return <Chip size="small" label={label} color={color} variant="filled" sx={{ fontWeight: 600 }} />;
  };

  const totals = React.useMemo(
    () => ({
      total: schedules.length,
      active: schedules.filter((s) => s.IsActive).length,
      inactive: schedules.filter((s) => !s.IsActive).length,
    }),
    [schedules]
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>Zamanlamalar</Typography>
          {!loading && schedules.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Toplam {totals.total} • {totals.active} aktif • {totals.inactive} pasif
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Listeyi yenile">
            <span>
              <IconButton size="small" onClick={reload} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
            Yeni Zamanlama
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && schedules.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            borderStyle: 'dashed',
            borderRadius: 2,
            background: 'linear-gradient(180deg,#fafafa 0%,#fff 100%)',
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(79,70,229,0.08)',
            }}
          >
            <EventBusyIcon sx={{ fontSize: 38, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Henüz zamanlama eklenmemiş
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 460, mx: 'auto' }}>
            Gün sonu raporlarının belirli saatlerde otomatik olarak email ile gönderilebilmesi için
            aşağıdaki butondan ilk zamanlamanızı oluşturun.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew} size="large">
            İlk Zamanlamayı Oluştur
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600 }}>Ad / Zamanlama</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tip</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Şubeler</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Alıcılar</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Son Çalışma</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sonraki</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (
                schedules.map((s) => {
                  const branchLabels = s.BranchIds.map((id) => getBranchName(id) || id);
                  const humanCron = humanizeCron(s.CronExpression);
                  return (
                    <TableRow key={s.Id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={s.Name ? 'text.primary' : 'text.secondary'}
                          fontStyle={s.Name ? 'normal' : 'italic'}
                        >
                          {s.Name || 'İsimsiz Rapor'}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap', rowGap: 0.5 }}>
                          {s.ReportKind && s.ReportKind !== 'endOfDay' && (
                            <Chip
                              size="small"
                              label={REPORT_KIND_OPTIONS.find((o) => o.kind === s.ReportKind)?.label || s.ReportKind}
                              variant="outlined"
                              sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                            />
                          )}
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {humanCron}
                            </Typography>
                          </Stack>
                        </Stack>
                        {s.UpdatedByEmail && (
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: 10, mt: 0.25 }}>
                            {s.UpdatedByEmail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{reportTypeChip(s.ReportType)}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        {branchLabels.length === 0 ? (
                          <Chip
                            size="small"
                            icon={<StorefrontIcon sx={{ fontSize: 14 }} />}
                            label="Tüm Şubeler"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Tooltip title={branchLabels.join(', ')} placement="top">
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                              {branchLabels.slice(0, 2).map((name, idx) => (
                                <Chip key={idx} size="small" label={name} variant="outlined" sx={{ maxWidth: 120 }} />
                              ))}
                              {branchLabels.length > 2 && (
                                <Chip size="small" label={`+${branchLabels.length - 2}`} variant="outlined" />
                              )}
                            </Stack>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                To: {s.Recipients.length === 0 ? '(yok)' : s.Recipients.join(', ')}
                              </Typography>
                              {s.CcRecipients.length > 0 && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                  CC: {s.CcRecipients.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          }
                          placement="top"
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <GroupIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption">
                              {s.Recipients.length}
                              {s.CcRecipients.length > 0 ? ` + ${s.CcRecipients.length} CC` : ''}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color={s.LastRunAt ? 'text.primary' : 'text.disabled'}>
                          {fmtDateTime(s.LastRunAt, s.TimeZoneId)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {s.IsActive && s.NextRunAt && (
                            <EventAvailableIcon sx={{ fontSize: 14, color: 'success.main' }} />
                          )}
                          <Typography variant="caption" color={s.NextRunAt ? 'text.primary' : 'text.disabled'}>
                            {s.IsActive ? fmtDateTime(s.NextRunAt, s.TimeZoneId) : 'Pasif'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {s.IsActive ? (
                          <Chip size="small" label="Aktif" color="success" sx={{ fontWeight: 600 }} />
                        ) : (
                          <Chip size="small" label="Pasif" color="default" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Test mail gönder">
                            <span>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleTestSend(s)}
                                disabled={testSendingId === s.Id}
                              >
                                {testSendingId === s.Id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Düzenle">
                            <IconButton size="small" onClick={() => handleEdit(s)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => requestDelete(s)}
                                disabled={deletingId === s.Id}
                              >
                                {deletingId === s.Id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ScheduleFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
        schedule={editingSchedule}
      />

      {/* Silme onay modali */}
      <Dialog
        open={deleteTarget !== null}
        onClose={cancelDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'rgba(244,67,54,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WarningAmberIcon sx={{ color: 'error.main' }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Zamanlamayı Sil
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {deleteTarget?.Name || 'İsimsiz Rapor'}
            </Box>{' '}
            adlı zamanlamayı silmek üzeresiniz.
          </DialogContentText>
          <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
            Bu işlem geri alınamaz. Zamanlama silinince Hangfire üzerinden otomatik mail gönderimi de
            duracaktır. Geçmiş çalıştırma logları korunur.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={cancelDelete}
            color="inherit"
            disabled={deletingId !== null}
          >
            Vazgeç
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            startIcon={deletingId !== null ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            disabled={deletingId !== null}
          >
            {deletingId !== null ? 'Siliniyor...' : 'Evet, Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {branches.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Şube listesi henüz yüklenmedi. Şube seçimi yapacağınız zaman görülebilir.
        </Alert>
      )}
    </Box>
  );
};

export default SchedulesTab;
